import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useCallback, useEffect, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { VOICES } from '../../data/content';
import { Reveal } from '../ui/Reveal';
import { Icons } from '../ui/Icons';

const easeOut = [0.22, 1, 0.36, 1] as const;

export function Families() {
  const { openLeadForm } = useApp();
  const reduce = useReducedMotion();
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = VOICES.length;

  const go = useCallback((next: number) => setActive(((next % count) + count) % count), [count]);

  // Gentle autoplay — pauses on hover/focus, disabled under reduced motion.
  useEffect(() => {
    if (reduce || paused) return;
    const t = window.setInterval(() => setActive((a) => (a + 1) % count), 7000);
    return () => window.clearInterval(t);
  }, [reduce, paused, count]);

  const slide = reduce ? 0 : 48;
  const voice = VOICES[active];

  return (
    <section id="families" className="relative py-20 sm:py-28 bg-mist">
      <div className="pointer-events-none absolute inset-0 bg-grain opacity-60" />
      <div className="container-rosa relative">
        <Reveal className="max-w-2xl">
          <p className="eyebrow">Who Rosa is for</p>
          <h2 className="mt-3 font-display text-4xl font-bold leading-[1.08] tracking-[-0.01em] text-plum sm:text-5xl">
            Built for the hardest week.
          </h2>
          <p className="mt-5 font-body text-lg leading-relaxed text-plum/70">
            These are the situations Rosa exists for. Not testimonials, but the real moments families
            find themselves in when a home suddenly has to be made safe. If one of them sounds like
            yours, you are exactly who we built this for.
          </p>
        </Reveal>

        <Reveal delay={0.1} className="mt-12">
          <div
            className="relative overflow-hidden rounded-[1.5rem] border border-stone bg-white shadow-lift"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onFocusCapture={() => setPaused(true)}
            onBlurCapture={() => setPaused(false)}
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={voice.id}
                initial={{ opacity: 0, x: slide }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -slide }}
                transition={{ duration: 0.5, ease: easeOut }}
                className="grid gap-0 lg:grid-cols-[0.4fr_0.6fr]"
              >
                {/* Image */}
                <div className="relative">
                  <img
                    src={voice.image}
                    alt=""
                    className="h-52 w-full object-cover sm:h-64 lg:h-full"
                    width={640}
                    height={800}
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-plum/25 via-transparent to-transparent lg:bg-gradient-to-r" />
                </div>

                {/* Copy */}
                <div className="p-7 sm:p-10">
                  <span className="inline-flex items-center rounded-full border border-sage/30 bg-sage/10 px-3 py-1 font-body text-xs font-semibold uppercase tracking-[0.14em] text-sage-deep">
                    {voice.tag}
                  </span>
                  <Icons.quote width={40} height={40} className="mt-5 text-terracotta" />
                  <blockquote className="mt-2 font-display text-2xl font-medium italic leading-snug text-plum sm:text-[1.7rem]">
                    {voice.quote}
                  </blockquote>
                  <div className="mt-6">
                    <p className="font-display text-lg font-semibold text-plum">{voice.name}</p>
                    <p className="mt-0.5 font-body text-sm text-plum/60">{voice.role}</p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Controls */}
          <div className="mt-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => go(active - 1)}
                aria-label="Previous situation"
                className="grid h-11 w-11 cursor-pointer place-items-center rounded-full border border-stone bg-white text-plum shadow-soft transition-colors hover:text-terracotta"
              >
                <Icons.arrowLeft width={20} height={20} />
              </button>
              <button
                onClick={() => go(active + 1)}
                aria-label="Next situation"
                className="grid h-11 w-11 cursor-pointer place-items-center rounded-full border border-stone bg-white text-plum shadow-soft transition-colors hover:text-terracotta"
              >
                <Icons.arrowRight width={20} height={20} />
              </button>
            </div>

            <div className="flex items-center gap-2">
              {VOICES.map((v, i) => (
                <button
                  key={v.id}
                  onClick={() => setActive(i)}
                  aria-label={`Go to situation ${i + 1}`}
                  aria-current={i === active}
                  className={`h-2.5 cursor-pointer rounded-full transition-all ${
                    i === active ? 'w-7 bg-terracotta' : 'w-2.5 bg-stone hover:bg-plum/20'
                  }`}
                />
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="mt-14 max-w-xl">
          <p className="font-body text-lg leading-relaxed text-plum/70">
            Whatever brought you here, the next step is the same and it is free.
          </p>
          <button onClick={() => openLeadForm()} className="btn-primary mt-6">
            Get a free safety assessment <Icons.arrowRight width={18} height={18} />
          </button>
        </Reveal>
      </div>
    </section>
  );
}
